import { QUESTION_TYPE } from '../constants/questionTypes';
import type { SurveyQuestion } from '../types';
import type { McqQuestion } from '../types/mcq';
import type { AnswerValue, SurveyAnswers, MatrixRowAnswers } from '../../submitSurvey/types';
import type { CustomFieldValueMap } from '../../surveyLogic/types/customFieldValues';
import { resolveCustomFieldPlaceholders } from './resolveCustomFieldPlaceholders';

const SURVEY_QUESTION_TOKEN = /\{\{SURVEY_QUESTION-:-([^}]+)\}\}/g;

function isMatrixType(type: SurveyQuestion['type']): boolean {
  return (
    type === QUESTION_TYPE.CFM_MATRIX ||
    type === QUESTION_TYPE.CSAT_MATRIX ||
    type === QUESTION_TYPE.RATING_MATRIX ||
    type === QUESTION_TYPE.SLIDER_MATRIX
  );
}

export function formatAnswerForDisplay(
  question: SurveyQuestion,
  answerValue: AnswerValue | undefined
): string {
  if (answerValue === undefined || answerValue === null) {
    return '';
  }

  if (question.type === QUESTION_TYPE.MCQ) {
    const mcq = question as McqQuestion;
    const selectedIds = Array.isArray(answerValue)
      ? answerValue
      : typeof answerValue === 'string'
        ? [answerValue]
        : [];

    const labels = selectedIds
      .map(id => mcq.options.find(o => o.id === id)?.optionLabel)
      .filter((label): label is string => Boolean(label));

    return labels.join(', ');
  }

  if (question.type === QUESTION_TYPE.TEXTFIELD && typeof answerValue === 'string') {
    return answerValue;
  }

  if (question.type === QUESTION_TYPE.NPS_SCALE && typeof answerValue === 'number') {
    return String(answerValue);
  }

  if (isMatrixType(question.type) && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
    const rowAnswers = answerValue as MatrixRowAnswers;
    return Object.values(rowAnswers)
      .filter(v => v !== undefined && v !== null && v !== '')
      .map(v => String(v))
      .join(', ');
  }

  if (typeof answerValue === 'string') {
    return answerValue;
  }

  if (typeof answerValue === 'number') {
    return String(answerValue);
  }

  return '';
}

export function resolveSurveyQuestionPlaceholders(
  html: string,
  answers: SurveyAnswers,
  questionsById: ReadonlyMap<string, SurveyQuestion>,
  customFieldValues?: CustomFieldValueMap
): string {
  const withCustomFields = customFieldValues
    ? resolveCustomFieldPlaceholders(html, customFieldValues)
    : html;

  return withCustomFields.replace(SURVEY_QUESTION_TOKEN, (match, questionId: string) => {
    const referencedQuestion = questionsById.get(questionId);
    if (!referencedQuestion) {
      return match;
    }

    return formatAnswerForDisplay(referencedQuestion, answers[questionId]);
  });
}
